// Textbaserad statistik baserad på Polisens händelse-summaries och artiklar.
// Vi parsar ord/fraser ur summary/name/body för att räkna fram förövar-/offer-relation,
// kön, vapenanvändning, stöldmetoder, fordonstyper, platser, utfall och mönster.
//
// Allt är heuristiskt — Polisens text är fri prosa. Vi gör ord-baserad
// matchning med ordgränser på lowercased text och räknar varje händelse
// max en gång per kategori.

import type { PoliceEvent } from "./polisen.functions";

const patternCache = new Map<string, RegExp>();

// Omfattande stoppordlista: filtrerar bort månader, veckodagar, klockslag,
// vanliga grammatiska ord och generiska termer som annars skräpar ner nyckelord.
const NOISE_WORDS = new Set([
  // Månader
  "januari", "februari", "mars", "april", "maj", "juni",
  "juli", "augusti", "september", "oktober", "november", "december",
  // Veckodagar
  "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag", "söndag",
  "måndags", "tisdags", "onsdags", "torsdags", "fredags", "lördags", "söndags",
  // Tid och siffror
  "klockan", "tiden", "minuter", "timmar", "dygn", "dygnet", "natten",
  "morgonen", "kvällen", "dagen", "förmiddagen", "eftermiddagen", "senare",
  "igår", "idag", "veckan", "helgen", "senaste",
  // Vanliga grammatiska ord & bindeord
  "och", "som", "med", "från", "till", "över", "under", "efter", "innan",
  "mellan", "mot", "vid", "utan", "inom", "på", "i", "en", "ett", "den",
  "det", "de", "du", "vi", "ni", "sig", "sin", "sitt", "sina", "han",
  "hon", "honom", "henne", "deras", "vår", "vårt", "våra", "era", "ert",
  "något", "någon", "några", "ingen", "inget", "inga", "alla", "allt",
  "därför", "dock", "också", "även", "redan", "genom", "eller", "men",
  "inte", "icke", "hade", "hade", "har", "vara", "varit", "blir", "blev",
  "blivit", "skall", "skulle", "kunna", "kunde", "kunnat", "måste",
  "vilket", "vilka", "vilken", "denna", "detta", "dessa", "samt",
  "cirka", "omkring", "ungefär", "mindre", "större", "flera", "andra",
  "samtidigt", "sedan", "efteråt", "strax", "både", "många", "mycket",
  // Generiska polishändelse-termer
  "polisen", "polis", "polisens", "poliser", "händelse", "händelser",
  "händelsen", "rapport", "rapporter", "rapporten", "plats", "platsen",
  "platser", "område", "områden", "området", "kommun", "kommunen",
  "sverige", "sveriges", "larm", "larmet", "larmades", "insats",
  "insatsen", "uppger", "uppgav", "enligt", "aktuell", "aktuella",
  "person", "personer", "personerna", "påbörjad", "avslutad", "inkommet",
  "gäller", "gällande", "angående", "avseende", "rubriceras", "rubricering",
  "anmälare", "anmälan", "anmälningar", "upprättad", "upprättas",
  "upprättat", "patrull", "patrullen", "patruller", "kontroll",
  "kontrolleras", "kontrollerade", "befinner", "befann", "lämnade",
  "lämnat", "färd", "riktning", "sammanfattning", "information",
  "kontakt", "kontakta", "tipsa", "uppgifter", "publicerad", "publicerade",
  "fortsatt", "arbetar", "arbetet", "utredning", "utredningen",
]);

function lower(e: PoliceEvent): string {
  return `${e.summary || ""} ${e.name || ""}`.toLowerCase();
}

/** Matcha någon av flera ord/fraser med ordgräns. */
function hasAny(text: string, words: string[]): boolean {
  for (const w of words) {
    let re = patternCache.get(w);
    if (!re) {
      re = new RegExp(`(^|[^\\p{L}])${escapeRe(w)}([^\\p{L}]|$)`, "iu");
      patternCache.set(w, re);
    }
    if (re.test(text)) return true;
  }
  return false;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export interface CountedItem {
  label: string;
  count: number;
}

function toSorted(map: Map<string, number>, minCount = 1): CountedItem[] {
  return Array.from(map.entries())
    .filter(([, c]) => c >= minCount)
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, count }));
}

/** Gör om [label, count]-par till sorterade signaler (tomma filtreras bort). */
function toSignals(pairs: Array<[string, number]>): CountedItem[] {
  return pairs
    .filter(([, count]) => count > 0)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

/** ===== Fordon ===== */
const VEHICLES: { label: string; words: string[] }[] = [
  { label: "Personbil", words: ["personbil", "personbilar", "bil", "bilar"] },
  { label: "Lastbil", words: ["lastbil", "lastbilar", "dragbil", "släp"] },
  { label: "Buss", words: ["buss", "bussar", "stadsbuss"] },
  { label: "Motorcykel (MC)", words: ["motorcykel", "motorcyklar", "mc"] },
  { label: "Moped / A-traktor", words: ["moped", "mopeder", "mopedbil", "a-traktor", "epa"] },
  { label: "Cykel / Elsparkcykel", words: ["cyklist", "cyklister", "cykel", "cyklar", "elsparkcykel", "elsparkcyklar"] },
  { label: "Fotgängare", words: ["fotgängare", "gångtrafikant", "gått på vägen"] },
  { label: "Husbil/husvagn", words: ["husbil", "husvagn"] },
];

function countVehicles(events: PoliceEvent[]): CountedItem[] {
  const m = new Map<string, number>();
  for (const e of events) {
    const t = lower(e);
    const seen = new Set<string>();
    for (const v of VEHICLES) {
      if (seen.has(v.label)) continue;
      if (hasAny(t, v.words)) {
        m.set(v.label, (m.get(v.label) || 0) + 1);
        seen.add(v.label);
      }
    }
  }
  return toSorted(m, 1);
}

/** ===== Platser & Miljöer ===== */
const COMMON_PLACES: { label: string; words: string[] }[] = [
  { label: "Bostad / Lägenhet", words: ["lägenhet", "lägenheter", "lägenheten", "trapphus"] },
  { label: "Villa / Radhus", words: ["villa", "villor", "radhus", "parhus"] },
  { label: "Krog / Nöjeslokal", words: ["krog", "krogen", "restaurang", "pub", "nattklubb", "bar", "servering"] },
  { label: "Butik / Galleria", words: ["butik", "butiken", "affär", "galleria", "köpcentrum", "kiosk", "mataffär", "livsmedelsbutik"] },
  { label: "Skola / Universitet", words: ["skola", "skolan", "förskola", "gymnasium", "skolgård", "högskola", "universitet"] },
  { label: "Kollektivtrafik / Station", words: ["tunnelbana", "t-bana", "pendeltåg", "tågstation", "resecentrum", "busshållplats", "perrong"] },
  { label: "Park / Grönområde", words: ["park", "parken", "grönområde", "skog", "skogsområde", "badplats", "motionsspår"] },
  { label: "Öppen gata / Torg", words: ["torg", "torget", "gågata", "gata", "gatan", "trottoar", "öppen gata", "allmän plats"] },
  { label: "Garage / Parkering", words: ["parkering", "parkeringsplats", "garage", "carport", "p-hus"] },
  { label: "Källare / Förråd", words: ["källare", "källarförråd", "vindsförråd", "förråd", "cykelförråd"] },
  { label: "Företag / Arbetsplats", words: ["företag", "kontor", "lager", "verkstad", "industri", "byggarbetsplats"] },
];

function countPlaces(events: PoliceEvent[], list: typeof COMMON_PLACES = COMMON_PLACES): CountedItem[] {
  const m = new Map<string, number>();
  for (const e of events) {
    const t = lower(e);
    const seen = new Set<string>();
    for (const p of list) {
      if (seen.has(p.label)) continue;
      if (hasAny(t, p.words)) {
        m.set(p.label, (m.get(p.label) || 0) + 1);
        seen.add(p.label);
      }
    }
  }
  return toSorted(m, 1);
}

/** ===== Sexualbrott & Våldtäkt ===== */
function sexualOffenceStats(events: PoliceEvent[]) {
  let relation = 0;
  let stranger = 0;
  let male = 0;
  let female = 0;
  let utomhus = 0;
  let inomhus = 0;
  let krog = 0;
  let forsok = 0;
  let overfall = 0;
  let ofredande = 0;
  let minderarig = 0;
  let gripen = 0;
  const ages: number[] = [];

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["närstående", "bekant", "relation", "sambo", "partner", "pojkvän", "flickvän", "make", "maka", "ex-partner"]))
      relation++;
    if (hasAny(t, ["obekant", "okänd", "okända", "överfall"]))
      stranger++;
    if (hasAny(t, ["man", "män", "manlig", "killar", "herre"])) male++;
    if (hasAny(t, ["kvinna", "kvinnor", "kvinnlig", "tjej", "dam"])) female++;
    if (hasAny(t, ["utomhus", "gata", "gatan", "park", "torg", "gångväg", "motionsspår", "skogsområde"])) utomhus++;
    if (hasAny(t, ["lägenhet", "bostad", "inomhus", "hem", "hotell", "festlokal"])) inomhus++;
    if (hasAny(t, ["krog", "nattklubb", "restaurang", "bar", "krogen", "dansgolv"])) krog++;
    if (hasAny(t, ["försök", "försökte"])) forsok++;
    if (hasAny(t, ["överfallsvåldtäkt", "överfall", "knivhot", "våld"])) overfall++;
    if (hasAny(t, ["ofredande", "blottare", "blottning", "antastat", "antastande", "ofredat", "fotografering"])) ofredande++;
    if (hasAny(t, ["flicka", "pojke", "barn", "ungdom", "under 15", "under 18", "minderårig"])) minderarig++;
    if (hasAny(t, ["gripen", "gripits", "anhållen", "anhållits", "häktad"])) gripen++;

    const age = t.match(/(\d{1,2})\s*[-\s]?(?:år|års|åring|årig|årsåldern)/);
    if (age) {
      const n = parseInt(age[1], 10);
      if (n >= 1 && n <= 99) ages.push(n);
    }
  }

  return {
    relation,
    stranger,
    male,
    female,
    utomhus,
    inomhus,
    krog,
    forsok,
    overfall,
    ofredande,
    minderarig,
    gripen,
    ages,
  };
}

/** ===== Bedrägeri & Åldringsbrott ===== */
function fraudStats(events: PoliceEvent[]) {
  let vishing = 0;
  let hembesok = 0;
  let aldre = 0;
  let net = 0;
  let fakeBankPolis = 0;
  let kort = 0;
  let forsok = 0;
  let fullbordat = 0;
  let gripen = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["ringde", "telefon", "uppringd", "vishing", "telefonbedrägeri"])) vishing++;
    if (hasAny(t, ["hembesök", "dörr", "släppte in", "ringde på", "dörren", "bostad"])) hembesok++;
    if (hasAny(t, ["äldre", "pensionär", "åldring", "äldre kvinna", "äldre man", "80-årsåldern", "90-årsåldern", "70-årsåldern"])) aldre++;
    if (hasAny(t, ["blocket", "marketplace", "swish", "internet", "handlat online", "fejkat"])) net++;
    if (hasAny(t, ["utgav sig", "banken", "banktjänsteman", "polis", "polisman", "säkerhetsavdelning", "inkasso"])) fakeBankPolis++;
    if (hasAny(t, ["bankkort", "smycken", "guld", "värdesaker", "kontanter", "bank-id", "bankid", "kod"])) kort++;
    if (hasAny(t, ["försök", "avbröts", "anade oråd", "genomskådade", "lurades inte"])) forsok++;
    if (hasAny(t, ["blev av med", "lämnade över", "överförde", "swishade", "uttag"])) fullbordat++;
    if (hasAny(t, ["gripen", "gripits", "anhållen", "identifierad"])) gripen++;
  }

  return { vishing, hembesok, aldre, net, fakeBankPolis, kort, forsok, fullbordat, gripen };
}

/** ===== Rån & Vapnat rån ===== */
function robberyStats(events: PoliceEvent[]) {
  let vapenSkjut = 0;
  let vapenKniv = 0;
  let vapenTillhygge = 0;
  let personRan = 0;
  let butiksRan = 0;
  let maskerade = 0;
  let fleraGarningspersoner = 0;
  let mobil = 0;
  let smyckenKlocka = 0;
  let jackaKlader = 0;
  let personskada = 0;
  let gripen = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["pistol", "vapen", "skjutvapen", "beväpnad", "pistolliknande"])) vapenSkjut++;
    if (hasAny(t, ["kniv", "knivhot", "knivbeväpnad"])) vapenKniv++;
    if (hasAny(t, ["tillhygge", "batong", "järnrör", "trubbigt"])) vapenTillhygge++;
    if (hasAny(t, ["på gatan", "utomhus", "personrån", "gick på", "överfölls"])) personRan++;
    if (hasAny(t, ["butik", "kiosk", "apotek", "bensinmack", "mataffär", "servering", "värdetransport"])) butiksRan++;
    if (hasAny(t, ["maskerad", "maskerade", "huvor", "balaklava", "munskydd"])) maskerade++;
    if (hasAny(t, ["flera gärningspersoner", "två personer", "tre personer", "flera personer", "gäng", "ungdomar"])) fleraGarningspersoner++;
    if (hasAny(t, ["mobil", "mobiltelefon", "telefon"])) mobil++;
    if (hasAny(t, ["klocka", "smycke", "smycken", "guldkedja", "armband"])) smyckenKlocka++;
    if (hasAny(t, ["jacka", "märkeskläder", "skor", "väska"])) jackaKlader++;
    if (hasAny(t, ["misshandlad", "skadad", "blödde", "ambulans", "slagen", "sparkad", "personskada"])) personskada++;
    if (hasAny(t, ["gripen", "gripits", "anhållen", "anhölls"])) gripen++;
  }

  return {
    vapenSkjut,
    vapenKniv,
    vapenTillhygge,
    personRan,
    butiksRan,
    maskerade,
    fleraGarningspersoner,
    mobil,
    smyckenKlocka,
    jackaKlader,
    personskada,
    gripen,
  };
}

/** ===== Misshandel ===== */
function assaultStats(events: PoliceEvent[]) {
  let relation = 0;
  let male = 0;
  let female = 0;
  let kniv = 0;
  let trubbigt = 0;
  let annatVapen = 0;
  let utomhus = 0;
  let inomhus = 0;
  let sjukhus = 0;
  let gripen = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["närstående", "relation", "sambo", "make", "maka", "fru", "pojkvän", "flickvän", "partner", "familj"]))
      relation++;
    if (hasAny(t, ["man", "män", "manlig", "kille"])) male++;
    if (hasAny(t, ["kvinna", "kvinnor", "kvinnlig", "tjej"])) female++;
    if (hasAny(t, ["kniv", "knivskuren", "knivhugg", "knivskärning"])) kniv++;
    if (hasAny(t, ["tillhygge", "trubbigt", "slag", "slagit", "slagen", "spark", "sparkar", "batong", "strypgrepp"]))
      trubbigt++;
    if (hasAny(t, ["flaska", "glas", "sten", "yxa", "hammare", "påk", "skruvmejsel"]))
      annatVapen++;
    if (hasAny(t, ["utomhus", "gata", "gatan", "torg", "park", "krogkroken"])) utomhus++;
    if (hasAny(t, ["lägenhet", "bostad", "inomhus", "hem", "trapphus"])) inomhus++;
    if (hasAny(t, ["ambulans", "sjukhus", "vård", "förd till sjukhus", "sydd", "allvarligt skadad"])) sjukhus++;
    if (hasAny(t, ["gripen", "gripits", "anhållen", "anhölls"])) gripen++;
  }

  return { relation, male, female, kniv, trubbigt, annatVapen, utomhus, inomhus, sjukhus, gripen };
}

/** ===== Brand & Mordbrand ===== */
function fireStats(events: PoliceEvent[]) {
  let anlagd = 0;
  let olycka = 0;
  let flerfamilj = 0;
  let villa = 0;
  let bil = 0;
  let soprum = 0;
  let skola = 0;
  let skog = 0;
  let evakuering = 0;
  let rok = 0;
  let tekniskUndersokning = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["anlagd", "mordbrand", "misstänks vara anlagd", "anmälning mordbrand", "allmänfarlig vårdslöshet"])) anlagd++;
    if (hasAny(t, ["ingen misstanke", "självantändning", "torrkokning", "olyckshändelse", "matlagning", "elektriskt"])) olycka++;
    if (hasAny(t, ["lägenhet", "flerfamiljshus", "hyreshus", "balkong"])) flerfamilj++;
    if (hasAny(t, ["villa", "radhus", "parhus", "lada", "gård"])) villa++;
    if (hasAny(t, ["bil", "bilar", "personbil", "fordon", "moped"])) bil++;
    if (hasAny(t, ["container", "soprum", "soptunna", "miljöhus", "papperskorg"])) soprum++;
    if (hasAny(t, ["skola", "förskola", "gymnasium", "idrottshall"])) skola++;
    if (hasAny(t, ["skog", "gräsbrand", "terräng", "mark"])) skog++;
    if (hasAny(t, ["evakuerades", "evakuering", "utrymning", "utrymdes"])) evakuering++;
    if (hasAny(t, ["rök", "rökskador", "rökutveckling", "rökinandning", "rökdykare"])) rok++;
    if (hasAny(t, ["teknisk undersökning", "avspärrat", "kriminaltekniker"])) tekniskUndersokning++;
  }

  return { anlagd, olycka, flerfamilj, villa, bil, soprum, skola, skog, evakuering, rok, tekniskUndersokning };
}

/** ===== Skadegörelse & Klotter ===== */
function vandalismStats(events: PoliceEvent[]) {
  let bilrutor = 0;
  let fonster = 0;
  let klotter = 0;
  let eldning = 0;
  let skolor = 0;
  let gripen = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["bilruta", "bilrutor", "krossad ruta bil", "däck", "backspegel"])) bilrutor++;
    if (hasAny(t, ["fönsterruta", "krossad ruta", "krossat fönster", "fönster", "entredörr"])) fonster++;
    if (hasAny(t, ["klotter", "spray", "sprayfärg", "klottrat", "tags"])) klotter++;
    if (hasAny(t, ["tänt på", "eldning", "bränt", "soptunna"])) eldning++;
    if (hasAny(t, ["skola", "skolan", "förskola"])) skolor++;
    if (hasAny(t, ["gripen", "påträffad", "identifierad", "ungdomar"])) gripen++;
  }

  return { bilrutor, fonster, klotter, eldning, skolor, gripen };
}

/** ===== Rattfylleri ===== */
function dwiStats(events: PoliceEvent[]) {
  let drog = 0;
  let alkohol = 0;
  let male = 0;
  let female = 0;
  let olycka = 0;
  let kontroll = 0;
  let korkort = 0;
  const ages: number[] = [];

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["drogpåverkad", "narkotika", "drog", "drograttfylleri", "cannabis", "kokain"])) drog++;
    if (hasAny(t, ["alkohol", "spritpåverkad", "berusad", "promille", "utandningsprov"])) alkohol++;
    if (hasAny(t, ["man", "män", "manlig", "herre", "kille"])) male++;
    if (hasAny(t, ["kvinna", "kvinnor", "kvinnlig", "dam", "tjej"])) female++;
    if (hasAny(t, ["olycka", "dikeskörning", "kollision", "körde av"])) olycka++;
    if (hasAny(t, ["rutinkontroll", "trafikkontroll", "stoppades", "kontroll"])) kontroll++;
    if (hasAny(t, ["omhandertogs", "indraget", "korkort", "körkort", "beslag"])) korkort++;

    const age = t.match(/(\d{2})\s*[-\s]?(?:år|års|åring|årig|årsåldern)/);
    if (age) {
      const n = parseInt(age[1], 10);
      if (n >= 15 && n <= 99) ages.push(n);
    }
  }

  return { drog, alkohol, male, female, olycka, kontroll, korkort, ages };
}

/** ===== Narkotika ===== */
function drugStats(events: PoliceEvent[]) {
  let brukInnehav = 0;
  let saljOverlatelse = 0;
  let male = 0;
  let female = 0;
  let beslag = 0;
  let gripen = 0;
  const ages: number[] = [];

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["eget bruk", "ringa", "innehav", "påverkad", "bruk"])) brukInnehav++;
    if (hasAny(t, ["överlåtelse", "försäljning", "distribution", "langning", "grovt", "narkotikahandel"])) saljOverlatelse++;
    if (hasAny(t, ["man", "män", "manlig"])) male++;
    if (hasAny(t, ["kvinna", "kvinnor", "kvinnlig"])) female++;
    if (hasAny(t, ["beslag", "beslagtogs", "hittades", "våg", "portionspåsar", "kontanter"])) beslag++;
    if (hasAny(t, ["gripen", "gripits", "anhållen", "anhölls"])) gripen++;

    const age = t.match(/(\d{2})\s*[-\s]?(?:år|års|åring|årig|årsåldern)/);
    if (age) {
      const n = parseInt(age[1], 10);
      if (n >= 15 && n <= 99) ages.push(n);
    }
  }

  return { brukInnehav, saljOverlatelse, male, female, beslag, gripen, ages };
}

/** ===== Inbrott / stöld ===== */
function burglaryStats(events: PoliceEvent[]) {
  let forsok = 0;
  let fullbordat = 0;
  let brytmärken = 0;
  let krossadruta = 0;
  let balkongStege = 0;
  let smyckenGuld = 0;
  let verktygMaskiner = 0;
  let cykel = 0;
  let bilElektronik = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["försök", "avbrutet"])) forsok++;
    else fullbordat++;
    if (hasAny(t, ["brytmärken", "brutit", "uppbruten", "kofot", "dörr"])) brytmärken++;
    if (hasAny(t, ["krossad ruta", "altandörr", "fönster", "inslagen"])) krossadruta++;
    if (hasAny(t, ["balkong", "stege", "klättrat", "andra våningen"])) balkongStege++;
    if (hasAny(t, ["smycken", "guld", "klockor", "armband", "vigselring"])) smyckenGuld++;
    if (hasAny(t, ["verktyg", "maskiner", "elverktyg", "byggmaskiner"])) verktygMaskiner++;
    if (hasAny(t, ["cykel", "elcykel", "cyklar"])) cykel++;
    if (hasAny(t, ["katalysator", "ratt", "gps", "dator", "elektronik", "ipad"])) bilElektronik++;
  }

  const places = countPlaces(events, [
    { label: "Villa / Radhus", words: ["villa", "villor", "radhus"] },
    { label: "Lägenhet", words: ["lägenhet", "lägenheter", "hyreshus"] },
    { label: "Källare / Förråd", words: ["källare", "källarförråd", "förråd", "vindsförråd"] },
    { label: "Garage / Fordon", words: ["garage", "bil", "fordon", "lastbil"] },
    { label: "Butik / Företag", words: ["butik", "företag", "kontor", "lager", "verkstad"] },
    { label: "Byggarbetsplats", words: ["byggarbetsplats", "bygge", "container"] },
  ]);

  return {
    forsok,
    fullbordat,
    brytmärken,
    krossadruta,
    balkongStege,
    smyckenGuld,
    verktygMaskiner,
    cykel,
    bilElektronik,
    places,
  };
}

/** ===== Mord/dråp ===== */
function homicideStats(events: PoliceEvent[]) {
  let skott = 0;
  let kniv = 0;
  let trubbigt = 0;
  let forsok = 0;
  let gripen = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["skott", "skottlossning", "skottskada", "skjutning", "eldvapen"])) skott++;
    if (hasAny(t, ["kniv", "knivhugg", "knivskuren"])) kniv++;
    if (hasAny(t, ["trubbigt", "slag", "strypning", "tillhygge"])) trubbigt++;
    if (hasAny(t, ["försök", "mordförsök", "dråpförsök"])) forsok++;
    if (hasAny(t, ["gripen", "gripits", "anhållen", "häktad"])) gripen++;
  }

  return { skott, kniv, trubbigt, forsok, gripen };
}

/** ===== Trafikolycka ===== */
function trafficAccidentStats(events: PoliceEvent[]) {
  let smitning = 0;
  let singel = 0;
  let kollision = 0;
  let personskada = 0;
  let algVilt = 0;
  let fotgangareCyklist = 0;

  for (const e of events) {
    const t = lower(e);
    if (hasAny(t, ["smitning", "smitare", "avvek"])) smitning++;
    if (hasAny(t, ["singelolycka", "singel", "avåkning", "diket"])) singel++;
    if (hasAny(t, ["kollision", "kolliderat", "frontalkollision", "påkörning bakifrån"])) kollision++;
    if (hasAny(t, ["personskada", "skadad", "skadade", "ambulans", "sjukhus"])) personskada++;
    if (hasAny(t, ["vilt", "viltolycka", "älg", "rådjur", "vildsvin"])) algVilt++;
    if (hasAny(t, ["cyklist", "fotgängare", "elsparkcykel", "påkörd gående"])) fotgangareCyklist++;
  }

  return { smitning, singel, kollision, personskada, algVilt, fotgangareCyklist, vehicles: countVehicles(events) };
}

function countTimePatterns(events: PoliceEvent[]) {
  const days = new Map<string, number>();
  const times = new Map<string, number>();
  const stockholmDateTime = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    weekday: "long",
    hour: "2-digit",
    hourCycle: "h23",
  });
  for (const event of events) {
    const date = new Date(event.datetime);
    if (Number.isNaN(date.getTime())) continue;
    const parts = stockholmDateTime.formatToParts(date);
    const dayPart = parts.find((part) => part.type === "weekday")?.value;
    const hourPart = parts.find((part) => part.type === "hour")?.value;
    if (!dayPart || hourPart === undefined) continue;
    const day = dayPart.charAt(0).toUpperCase() + dayPart.slice(1);
    const hour = Number(hourPart);
    const time =
      hour < 6
        ? "Natt (00–05)"
        : hour < 12
          ? "Morgon (06–11)"
          : hour < 18
            ? "Dag (12–17)"
            : "Kväll (18–23)";
    days.set(day, (days.get(day) || 0) + 1);
    times.set(time, (times.get(time) || 0) + 1);
  }
  return { weekdays: toSorted(days), timeOfDay: toSorted(times) };
}

function countKnownPlaces(events: PoliceEvent[]): CountedItem[] {
  const places = new Map<string, number>();
  for (const event of events) {
    const label = event.place_label?.trim();
    if (!label || label.length < 3) continue;
    places.set(label, (places.get(label) || 0) + 1);
  }
  return toSorted(places, 2).slice(0, 8);
}

const OUTCOMES = [
  { label: "Gripen eller anhållen", words: ["gripen", "gripits", "anhållen", "anhållits", "häktad"] },
  { label: "Fördes till sjukhus / vård", words: ["ambulans", "sjukhus", "förd till vård", "fördes till vård", "akuten"] },
  { label: "Personskada rapporterad", words: ["personskada", "skadad", "skadade", "skadats", "blödande"] },
  { label: "Teknisk undersökning / Avspärrning", words: ["avspärrat", "teknisk undersökning", "kriminaltekniker", "spårsäkring"] },
  { label: "Beslag av föremål / stöldgods", words: ["beslag", "beslagtogs", "beslagtagits"] },
];

function countOutcomes(events: PoliceEvent[]): CountedItem[] {
  return countPlaces(events, [...OUTCOMES]);
}

/**
 * Räknar fram relevanta nyckelord utan nonsens/månader/fyllnadsord.
 */
function countKeywords(events: PoliceEvent[]): CountedItem[] {
  const counts = new Map<string, number>();
  for (const event of events) {
    const text = lower(event);
    const seen = new Set<string>();
    const tokens = text.match(/\p{L}{4,}/gu) ?? [];
    for (const token of tokens) {
      const word = token.toLowerCase();
      if (NOISE_WORDS.has(word)) continue;
      if (word.length < 4) continue;
      if (seen.has(word)) continue;
      counts.set(word, (counts.get(word) || 0) + 1);
      seen.add(word);
    }
  }
  return toSorted(counts, 2).slice(0, 8);
}

export interface CrimeTypeInsightData {
  // Gemensamma
  vehicles?: CountedItem[];
  places?: CountedItem[];
  areas?: CountedItem[];
  keywords?: CountedItem[];
  signals?: CountedItem[];
  ageBuckets?: { label: string; count: number }[];
  weekdays: CountedItem[];
  timeOfDay: CountedItem[];
  knownPlaces: CountedItem[];
  outcomes: CountedItem[];
  analyzedFrom: string;
  analyzedTo: string;

  // Sexualbrott
  sexualStats?: ReturnType<typeof sexualOffenceStats>;
  // Bedrägeri
  fraudStats?: ReturnType<typeof fraudStats>;
  // Rån
  robberyStats?: ReturnType<typeof robberyStats>;
  // Misshandel
  assaultStats?: ReturnType<typeof assaultStats>;
  // Brand
  fireStats?: ReturnType<typeof fireStats>;
  // Skadegörelse
  vandalismStats?: ReturnType<typeof vandalismStats>;
  // Rattfylleri
  dwiStats?: ReturnType<typeof dwiStats>;
  // Narkotika
  drugStats?: ReturnType<typeof drugStats>;
  // Inbrott / stöld
  burglaryStats?: ReturnType<typeof burglaryStats>;
  // Mord / dråp
  homicideStats?: ReturnType<typeof homicideStats>;
  // Trafikolycka
  trafficStats?: ReturnType<typeof trafficAccidentStats>;
}

export function getInsightSummary(insights: CrimeTypeInsightData | null): string | null {
  if (!insights) return null;
  const topTime = insights.timeOfDay[0];
  const topPlace = insights.knownPlaces[0] ?? insights.places?.[0];
  const topOutcome = insights.outcomes[0];
  const parts: string[] = [];
  if (topTime) parts.push(`flest rapporter under ${topTime.label.toLowerCase()}`);
  if (topPlace) parts.push(`${topPlace.label} är den vanligaste identifierade platsen`);
  if (topOutcome)
    parts.push(`${topOutcome.label.toLowerCase()} nämns i ${topOutcome.count} rapporter`);
  return parts.length > 0 ? parts.slice(0, 2).join(" och ") : null;
}

function sharedInsights(events: PoliceEvent[]) {
  const sortedDates = events
    .map((event) => event.datetime)
    .filter(Boolean)
    .sort();
  const time = countTimePatterns(events);
  return {
    ...time,
    knownPlaces: countKnownPlaces(events),
    outcomes: countOutcomes(events),
    keywords: countKeywords(events),
    places: countPlaces(events),
    analyzedFrom: sortedDates[0] ?? "",
    analyzedTo: sortedDates[sortedDates.length - 1] ?? "",
  };
}

function bucketAges(ages: number[]): { label: string; count: number }[] {
  if (ages.length === 0) return [];
  const buckets = [
    { label: "Under 18 år", min: 0, max: 17 },
    { label: "18–24 år", min: 18, max: 24 },
    { label: "25–34 år", min: 25, max: 34 },
    { label: "35–49 år", min: 35, max: 49 },
    { label: "50–64 år", min: 50, max: 64 },
    { label: "65+ år", min: 65, max: 120 },
  ];
  return buckets
    .map((b) => ({ label: b.label, count: ages.filter((a) => a >= b.min && a <= b.max).length }))
    .filter((b) => b.count > 0);
}

/**
 * Returnerar insikter baserade på händelsetyp och polisens faktiska texter.
 */
export function getInsightsForType(
  type: string,
  events: PoliceEvent[],
): CrimeTypeInsightData | null {
  if (events.length === 0) return null;
  const t = type.toLowerCase();
  const shared = sharedInsights(events);

  // Sexualbrott & Våldtäkt
  if (t.includes("sexual") || t.includes("våldtäkt") || t.includes("sexuellt") || t.includes("ofredande")) {
    const s = sexualOffenceStats(events);
    return {
      ...shared,
      sexualStats: s,
      ageBuckets: bucketAges(s.ages),
      signals: toSignals([
        ["Närstående / bekant relation", s.relation],
        ["Okänd gärningsperson / obekant", s.stranger],
        ["Utomhusmiljö (park/gata/torg)", s.utomhus],
        ["Inomhusmiljö / bostad", s.inomhus],
        ["Krog- / nöjesmiljö", s.krog],
        ["Minderårig inblandad", s.minderarig],
        ["Överfall / fysiskt våld", s.overfall],
        ["Ofredande / blottning", s.ofredande],
        ["Försök", s.forsok],
        ["Gripande / anhållande", s.gripen],
      ]),
    };
  }

  // Bedrägeri
  if (t.includes("bedrägeri") || t.includes("åldringsbrott")) {
    const s = fraudStats(events);
    return {
      ...shared,
      fraudStats: s,
      signals: toSignals([
        ["Telefonbedrägeri / vishing", s.vishing],
        ["Hembesök hos målsägande", s.hembesok],
        ["Riktat mot äldre person", s.aldre],
        ["Utgav sig för att vara bank/polis", s.fakeBankPolis],
        ["Bankkort / smycken / koder", s.kort],
        ["Nät- / köpbedrägeri", s.net],
        ["Försök som avvärjdes", s.forsok],
        ["Fullbordad förlust", s.fullbordat],
        ["Misstänkt gripen", s.gripen],
      ]),
    };
  }

  // Rån & Vapnat rån
  if (t.includes("rån")) {
    const s = robberyStats(events);
    return {
      ...shared,
      robberyStats: s,
      signals: toSignals([
        ["Rån mot person på allmän plats", s.personRan],
        ["Rån mot butik / verksamhet", s.butiksRan],
        ["Skjutvapen / pistolliknande föremål", s.vapenSkjut],
        ["Knivbeväpning", s.vapenKniv],
        ["Tillhygge / trubbigt våld", s.vapenTillhygge],
        ["Flera gärningspersoner / gäng", s.fleraGarningspersoner],
        ["Maskerade förövare", s.maskerade],
        ["Byte: Mobiltelefon", s.mobil],
        ["Byte: Klocka / smycken", s.smyckenKlocka],
        ["Byte: Kläder / jacka / väska", s.jackaKlader],
        ["Fysisk personskada", s.personskada],
        ["Gripande / anhållande", s.gripen],
      ]),
    };
  }

  // Misshandel & Våld
  if (t.includes("misshandel") || t.includes("våld")) {
    const s = assaultStats(events);
    return {
      ...shared,
      assaultStats: s,
      signals: toSignals([
        ["Närstående / parrelation", s.relation],
        ["Utomhus (gata/torg/park)", s.utomhus],
        ["Inomhus / bostadsmiljö", s.inomhus],
        ["Kniv / stickvapen", s.kniv],
        ["Slag, sparkar & trubbigt våld", s.trubbigt],
        ["Glas, flaska eller tillhygge", s.annatVapen],
        ["Sjukhusvård / ambulans", s.sjukhus],
        ["Misstänkt gripen", s.gripen],
      ]),
    };
  }

  // Brand & Mordbrand
  if (t.includes("brand")) {
    const s = fireStats(events);
    return {
      ...shared,
      fireStats: s,
      vehicles: countVehicles(events),
      signals: toSignals([
        ["Anlagd brand / mordbrand misstänks", s.anlagd],
        ["Olyckshändelse / ingen brottsmisstanke", s.olycka],
        ["Flerfamiljshus / lägenhet", s.flerfamilj],
        ["Villa / radhus / gård", s.villa],
        ["Bil- / fordonsbrand", s.bil],
        ["Soprum / container", s.soprum],
        ["Skola / offentlig byggnad", s.skola],
        ["Skog- / markbrand", s.skog],
        ["Kraftig rökutveckling", s.rok],
        ["Evakuering / utrymning", s.evakuering],
        ["Kriminalteknisk undersökning", s.tekniskUndersokning],
      ]),
    };
  }

  // Skadegörelse & Klotter
  if (t.includes("skadegörelse") || t.includes("klotter")) {
    const s = vandalismStats(events);
    return {
      ...shared,
      vandalismStats: s,
      signals: toSignals([
        ["Krossade bilrutor / fordonsskada", s.bilrutor],
        ["Krossade fönster / fastighet", s.fonster],
        ["Klotter / sprayfärg / tags", s.klotter],
        ["Skadegörelse genom brand", s.eldning],
        ["Drabbat skola / förskola", s.skolor],
        ["Misstänkt identifierad / gripen", s.gripen],
      ]),
    };
  }

  // Inbrott & Stöld
  if (t.includes("inbrott") || t.includes("stöld") || t.includes("tillgrepp")) {
    const s = burglaryStats(events);
    return {
      ...shared,
      burglaryStats: s,
      places: s.places,
      signals: toSignals([
        ["Fullbordat tillgrepp", s.fullbordat],
        ["Försök till inbrott / stöld", s.forsok],
        ["Brytmärken / dörr bryts upp", s.brytmärken],
        ["Krossat fönster / altandörr", s.krossadruta],
        ["Klättrat via balkong / stege", s.balkongStege],
        ["Stöldgods: Smycken & guld", s.smyckenGuld],
        ["Stöldgods: Verktyg & maskiner", s.verktygMaskiner],
        ["Stöldgods: Cykel / elcykel", s.cykel],
        ["Stöldgods: Bildelar / elektronik", s.bilElektronik],
      ]),
    };
  }

  // Rattfylleri
  if (t.includes("rattfylleri") || t.includes("drograttfylleri")) {
    const s = dwiStats(events);
    return {
      ...shared,
      dwiStats: s,
      ageBuckets: bucketAges(s.ages),
      signals: toSignals([
        ["Alkoholpåverkan", s.alkohol],
        ["Drogpåverkan / narkotika", s.drog],
        ["Uppdagades vid trafikkontroll", s.kontroll],
        ["Inblandad i trafikolycka", s.olycka],
        ["Omhändertaget körkort / fordon", s.korkort],
        ["Nämner manlig förare", s.male],
        ["Nämner kvinnlig förare", s.female],
      ]),
    };
  }

  // Narkotika
  if (t.includes("narkotika")) {
    const s = drugStats(events);
    return {
      ...shared,
      drugStats: s,
      ageBuckets: bucketAges(s.ages),
      signals: toSignals([
        ["Eget bruk / innehav", s.brukInnehav],
        ["Försäljning / överlåtelse / langning", s.saljOverlatelse],
        ["Beslag av narkotika / kontanter", s.beslag],
        ["Misstänkt gripen / anhållen", s.gripen],
        ["Nämner man", s.male],
        ["Nämner kvinna", s.female],
      ]),
    };
  }

  // Mord & Dråp
  if (t.includes("mord") || t.includes("dråp")) {
    const s = homicideStats(events);
    return {
      ...shared,
      homicideStats: s,
      signals: toSignals([
        ["Skjutvapen / skottlossning", s.skott],
        ["Knivvåld", s.kniv],
        ["Trubbigt våld / slag", s.trubbigt],
        ["Försök till mord / dråp", s.forsok],
        ["Misstänkt gripen / anhållen", s.gripen],
      ]),
    };
  }

  // Trafikolyckor & Smitningar
  if (t.includes("trafik") || t.includes("smitning")) {
    const s = trafficAccidentStats(events);
    return {
      ...shared,
      trafficStats: s,
      vehicles: s.vehicles,
      signals: toSignals([
        ["Kollision mellan fordon", s.kollision],
        ["Singelolycka / avåkning", s.singel],
        ["Personskador / sjukhusvård", s.personskada],
        ["Smitningsolycka", s.smitning],
        ["Påkörd gående eller cyklist", s.fotgangareCyklist],
        ["Viltolycka", s.algVilt],
      ]),
    };
  }

  return shared;
}
